<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { formsApi, type Form } from '@/services/api/forms';
import { useAppConfirm } from '@/composables/confirm';
import { useNotifier } from '@/composables/notifier';
import ProjectsSubNav from './ProjectsSubNav.vue';

const route = useRoute();
const forms = ref<Form[]>([]);
const confirm = useAppConfirm();
const notify = useNotifier();

function projectIdParam(): string {
  const raw = route.params.id;
  return Array.isArray(raw) ? (raw[0] ?? '') : raw ?? '';
}

async function reload(): Promise<void> {
  forms.value = await formsApi.index(projectIdParam());
}

async function remove(form: Form): Promise<void> {
  const ok = await confirm({
    header: 'Remove Form',
    message: 'Are you sure you want to remove this form?',
  });
  if (!ok) return;
  await formsApi.remove(projectIdParam(), form.id);
  forms.value = forms.value.filter((f) => f.id !== form.id);
  notify.success('Form removed');
}

async function publish(form: Form): Promise<void> {
  const isValid = await formsApi.validate(projectIdParam(), form.id);
  if (!isValid) {
    notify.error('Form must have at least one section and one control', 'Cannot Publish Form');
    return;
  }
  await formsApi.publish(projectIdParam(), form.id);
  notify.success('Form published');
  await reload();
}

onMounted(reload);
</script>

<template>
  <div>
    <ProjectsSubNav :project-id="projectIdParam()" />
    <section class="resource">
      <header class="resource__header">
        <h1>Forms</h1>
        <RouterLink
          :to="{ name: 'projectFormsAdd', params: { id: projectIdParam() } }"
          class="resource__cta"
        >
          + New Form
        </RouterLink>
      </header>

      <ul class="resource__list">
        <li v-for="form in forms" :key="form.id">
          <RouterLink :to="`/projects/${projectIdParam()}/forms/${form.id}`">
            {{ form.name }}
            <small v-if="form.published">• published</small>
          </RouterLink>
          <RouterLink :to="`/projects/${projectIdParam()}/forms/${form.id}/edit`">Edit</RouterLink>
          <RouterLink :to="`/projects/${projectIdParam()}/forms/${form.id}/clone`">Clone</RouterLink>
          <button type="button" @click="publish(form)">Publish</button>
          <button type="button" @click="remove(form)">Remove</button>
        </li>
      </ul>
      <p v-if="forms.length === 0">No forms.</p>
    </section>
  </div>
</template>

<style scoped src="@/assets/styles/resource-list.scss"></style>
<style scoped>
.resource__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}
.resource__cta {
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: #fff;
  border-radius: 0.375rem;
  text-decoration: none;
}
.resource__list a {
  color: #1f2937;
  text-decoration: none;
}
.resource__list small {
  color: #6b7280;
}
</style>
